import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import DeleteDialogIcon from "@entrilia/shared/icons/DeleteDialogIcon";
import type { CreateApprovalConfigurationVersionRequest } from "../../../../api/types/workflowTypes";
import ApprovalConfigurationContext from "./ApprovalConfigurationContext";

interface Props {
  approvalStepName: string;
  open: boolean;
  onClose: () => void;
}

const DeleteApprovalStepDialog = ({ approvalStepName, open, onClose }: Props) => {
  const { approvalConfiguration, isDraft, saveConfiguration } = useDefinedContext(ApprovalConfigurationContext);

  const [loading, setLoading] = useState(false);

  if (approvalStepName === undefined) {
    return null;
  }

  const handleDelete = async () => {
    const payload: CreateApprovalConfigurationVersionRequest = {
      steps: approvalConfiguration.steps.filter((step) => step.name !== approvalStepName),
      conversionTargetCurrencyCode: approvalConfiguration.conversionTargetCurrencyCode,
      approversAssignmentMode: approvalConfiguration.approversAssignmentMode,
    };

    setLoading(true);
    const saved = await saveConfiguration(payload, "Approval step deleted successfully");
    setLoading(false);

    if (saved) {
      onClose();
    }
  };

  return (
    <SmallDialog open={open} onClose={onClose}>
      <DialogTitle>Delete Approval Step</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <DeleteDialogIcon sx={{ width: 80, height: 80 }} />
          <Stack spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="h6">Delete {approvalStepName}?</Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              {isDraft
                ? "You're about to delete this approval step. It will be removed when you save the approval configuration."
                : "You're about to delete this approval step. This action is permanent and can't be undone."}
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button variant="outlined" color="error" loading={loading} onClick={handleDelete}>
          Delete
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default DeleteApprovalStepDialog;
